import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

type Language = 'en' | 'zh';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    login: 'Login ',
    username: 'Username 用戶名',
    password: 'Password 密碼',
    enterUsername: 'Enter username',
    enterPassword: 'Enter password',
    loginButton: 'Login',
    continueWithoutLogin: 'Continue without login',
    accessCMS: 'Access your CMS application',
    clientList: 'Client List 客戶列表',
    engagement: 'Engagement 項目',
    invoice: 'Invoice',
    invoicei: 'Invoice',
    kpi: 'KPI',
    timeSheet: 'Time Sheet 時間表',
    annualLeave: 'Annual Leave System 年假系統',
    calendar: 'Calendar 日曆',
    logout: 'Logout',
    addNewClient: 'Add New Client 新增客戶',
    searchAuditClientCode: 'Audit Client Code',
    searchCompanySecretarialCode: 'Company Secretarial Code',
    searchCompanyName: 'Company Name',
    searchBRNumber: 'BR Number',
    auditClientCode: 'Audit Client Code',
    companySecretarialClientCode: 'Company Secretarial Client Code',
    companyName: 'Company Name',
    partner: 'Partner',
    group: 'Group',
    brNumber: 'BR Number',
    stockTake: 'Stock Take',
    latestAnnualAuditYearEndDate: 'Latest Annual Audit Year End Date',
    taxReturnsOutstanding: 'Tax Returns Outstanding',
    annualReturnAlreadyFiled: 'Annual Return already filed in CR',
    agent: 'Agent',
    registeredOffice: 'Registered Office',
    clientContact: 'Client Contact',
    active: 'Active',
    services: 'Services',
    yes: 'Yes',
    no: 'No',
    addClient: 'Add New Client',
    companyMustStartWithLetter: 'Company name must start with an English letter',
    servicesRequired: 'Please select at least one of: audit, account only, account + audit, or company secretary',
    audit: 'Audit',
    accountOnly: 'Account Only',
    accountPlusAudit: 'Account + Audit',
    companySecretary: 'Company Secretary',
    advisory: 'Advisory',
    taxation: 'Taxation',
    cancel: 'Cancel',
    add: 'Add',
    search: 'Search',
    edit: 'Edit',
    list: 'List',
    preview: 'Preview',
    details: 'Details',
    close: 'Close',
    print: 'Print',
    workCalendar: 'Work calendar showing engagements and their scheduled work/projects',
    previousWeek: 'Previous Week 上一週',
    nextWeek: 'Next Week 下一週',
    to: 'to',
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    description: 'Description',
    value: 'Value',
    unit: 'Unit',
    member: 'Member',
    projectsFor: 'Projects for',
    project: 'Project',
    auditEngagement: 'Audit Engagement 審計項目',
    accountOnlyEngagement: 'Account Only Engagement 僅會計項目',
    companySecretaryEngagement: 'Company Secretary Engagement 公司秘書項目',
    annualLeavePerYear: 'Annual leave per year 每年年假',
    joiningDate: 'Joining Date 入職日期',
    invoiceList: 'Invoice List 發票列表',
    usernameRequired: 'Username is required',
    passwordRequired: 'Password is required',
    invalidUsername: 'Invalid username',
    invalidCredentials: 'Invalid username or password',
    quickLogin: 'Or quick login with',
    welcome: 'Welcome',
    lockedBy: 'Locked by',
    editingBy: 'Editing by',
    rowLockedBy: 'Row is locked by',
    addAnnualLeave: 'Add Annual Leave',
    deleteAnnualLeave: 'Delete Annual Leave',
    previous: 'Previous',
    next: 'Next',
    noAnnualLeaveAccess: 'You do not have access to annual leave data',
    clientDetails: 'Client Details 客戶詳情',
  },
  zh: {
    login: '登入',
    username: '用戶名',
    password: '密碼',
    enterUsername: '輸入用戶名',
    enterPassword: '輸入密碼',
    loginButton: '登入',
    continueWithoutLogin: '無需登入繼續',
    accessCMS: '訪問您的CMS應用程式',
    clientList: '客戶列表',
    engagement: '項目',
    invoice: '發票',
    invoicei: '發票',
    kpi: '關鍵績效指標',
    timeSheet: '時間表',
    annualLeave: '年假系統',
    calendar: '日曆',
    logout: '登出',
    addNewClient: '新增客戶',
    searchAuditClientCode: '審計客戶代碼',
    searchCompanySecretarialCode: '公司秘書客戶代碼',
    searchCompanyName: '公司名稱',
    searchBRNumber: '商業登記號碼',
    auditClientCode: '審計客戶代碼',
    companySecretarialClientCode: '公司秘書客戶代碼',
    companyName: '公司名稱',
    partner: '合夥人',
    group: '組別',
    brNumber: '商業登記號碼',
    stockTake: '盤點',
    latestAnnualAuditYearEndDate: '最新年度審計年結日期',
    taxReturnsOutstanding: '未完成的稅務申報',
    annualReturnAlreadyFiled: '已在公司註冊處提交年度申報表',
    agent: '代理人',
    registeredOffice: '註冊辦事處',
    clientContact: '客戶聯絡人',
    active: '活躍',
    services: '服務',
    yes: '是',
    no: '否',
    addClient: '新增客戶',
    companyMustStartWithLetter: '公司名稱必須以英文字母開頭',
    servicesRequired: '請選擇至少一項：審計、僅會計、會計+審計或公司秘書',
    audit: '審計',
    accountOnly: '僅會計',
    accountPlusAudit: '會計+審計',
    companySecretary: '公司秘書',
    advisory: '諮詢',
    taxation: '稅務',
    cancel: '取消',
    add: '新增',
    search: '搜尋',
    edit: '編輯',
    list: '列表',
    preview: '預覽',
    details: '詳情',
    close: '關閉',
    print: '打印',
    workCalendar: '工作日曆顯示項目及其安排的工作/項目',
    previousWeek: '上週',
    nextWeek: '下週',
    to: '至',
    sunday: '日',
    monday: '一',
    tuesday: '二',
    wednesday: '三',
    thursday: '四',
    friday: '五',
    saturday: '六',
    description: '描述',
    value: '數值',
    unit: '單位',
    member: '成員',
    projectsFor: '項目為',
    project: '項目',
    auditEngagement: '審計項目',
    accountOnlyEngagement: '僅會計項目',
    companySecretaryEngagement: '公司秘書項目',
    annualLeavePerYear: '每年年假',
    joiningDate: '入職日期',
    invoiceList: '發票列表',
    usernameRequired: '用戶名是必需的',
    passwordRequired: '密碼是必需的',
    invalidUsername: '用戶名無效',
    invalidCredentials: '用戶名或密碼無效',
    quickLogin: '或者快速登入',
    welcome: '歡迎',
    lockedBy: '被鎖定由',
    editingBy: '正在編輯由',
    rowLockedBy: '該行被鎖定由',
    addAnnualLeave: '增加年假',
    deleteAnnualLeave: '刪除年假',
    previous: '上一頁',
    next: '下一頁',
    noAnnualLeaveAccess: '您沒有權限查看年假數據',
    clientDetails: '客戶詳情',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'zh' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.en] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};